import { Inject, Injectable } from '@angular/core';
import { EmailTemplates, LogMessage, LogMessageSourceTypes, LogMessageTypes, SendOTP } from '@ag-common-lib/public-api';
import { differenceInSeconds } from 'date-fns';
import { FirebaseApp } from 'firebase/app';
import { BehaviorSubject, Observable } from 'rxjs';
import { CommonFireStoreDao, QueryParam } from '../dao/CommonFireStoreDao.dao';
import { FIREBASE_APP } from '../injections/firebase-app';
import { CloudFunctionsService } from './cloud-functions.service';
import { LoggerService } from './logger.service';
import { ToastrService } from 'ngx-toastr';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable()
export class OtpService {
  private readonly RESENT_TIMEOUT = 120;

  private readonly fsDao: CommonFireStoreDao<any>;
  private collection = 'otp';

  public isResendAvailable$: Observable<boolean>;
  private readonly _isResendAvailable$ = new BehaviorSubject<boolean>(true);

  public resendTimeout$: Observable<number>;
  private readonly _resendTimeout$ = new BehaviorSubject<number>(this.RESENT_TIMEOUT);

  public isSendOTPInProgress$: Observable<boolean>;
  private readonly _isSendOTPInProgress$ = new BehaviorSubject<boolean>(false);

  public isOTPSended$: Observable<boolean>;
  private readonly _isOTPSended$ = new BehaviorSubject<boolean>(false);
  private _lastCountdownValue: Date;

  private readonly _otpRef$ = new BehaviorSubject<string>(null);
  otpRefSig = toSignal(this._otpRef$);

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
    private loggerService: LoggerService,
    private toastrService: ToastrService,
    private cloudFunctionsService: CloudFunctionsService,
  ) {
    this.fsDao = new CommonFireStoreDao<any>(fireBaseApp);

    this.isSendOTPInProgress$ = this._isSendOTPInProgress$.asObservable();
    this.isOTPSended$ = this._isOTPSended$.asObservable();
    this.isResendAvailable$ = this._isResendAvailable$.asObservable();
    this.resendTimeout$ = this._resendTimeout$.asObservable();
  }

  getList(qp: QueryParam[] = []) {
    const path = this.collection;
    return this.fsDao.getList(path, qp);
  }

  // Validation runs server-side (validateOTP function, Admin SDK) so the client
  // never reads the otp collection directly. Reading it here previously required
  // the collection to allow anonymous reads for the unauthenticated
  // forgot-password flow, which exposed every active reset code to the internet.
  validateOTP = async (otp, email): Promise<boolean | void> => {
    if (!email) {
      await this.loggerService.createLog(
        LogMessageTypes.otpValidation,
        LogMessageSourceTypes.system,
        'system',
        'Failed to Detect Google User Email',
        null,
      );

      return false;
    }

    let result: { isValid: boolean; reason?: string };

    try {
      result = await this.cloudFunctionsService.validateOTP({ otp, email });
    } catch (getOtpError) {
      await this.loggerService.createLog(
        LogMessageTypes.otpValidation,
        LogMessageSourceTypes.email,
        email,
        'Get OTP data issue detected',
        { otp, email, getOtpError: (getOtpError as Error)?.message ?? getOtpError },
      );

      return false;
    }

    if (result?.isValid) {
      return true;
    }

    if (result?.reason === 'expired') {
      await this.loggerService.createLog(
        LogMessageTypes.otpValidation,
        LogMessageSourceTypes.email,
        email,
        'Confirmation Code Is Expired',
        { otp, email, dateNow: new Date() },
      );

      this.toastrService.error(
        'Entered confirmation code is expired. Please note that code is valid during 30 minutes.',
        'One Time Code Is Expired',
      );
      return false;
    }

    await this.loggerService.createLog(
      LogMessageTypes.otpValidation,
      LogMessageSourceTypes.email,
      email,
      'One Time Code is Invalid',
      { otp, email, reason: result?.reason },
    );

    this.toastrService.error(
      'Entered code is out-of-date. Please check your inbox for the latest code.',
      'One Time Code Invalid',
    );
    return false;
  };

  public sendOtp = async (email: string, payload: Partial<SendOTP>) => {
    const { duration = 30, subject: title = 'Confirm Your Email', emailTemplate, isPermanent, bcc } = payload;
    const otpRef = this.genRef();
    const subject = isPermanent ? 'Confirmation Code for Agent Portal' : `Request ID ${otpRef}: ${title}`;

    try {
      this._otpRef$.next(otpRef);
      this._isOTPSended$.next(false);
      this._isSendOTPInProgress$.next(true);
      this._isResendAvailable$.next(false);

      this.cloudFunctionsService
        .sendOTP({ email, duration, emailTemplate, isPermanent, subject, otpRef, bcc })
        .then(data => {
          this.startCountdown();
          this._isOTPSended$.next(true);

          return data;
        })
        .catch(e => {
          this._isResendAvailable$.next(true);
          throw e;
        })
        .finally(() => {
          this._isSendOTPInProgress$.next(false);
        });

      return otpRef;
    } catch (error) {
      this.toastrService.error('Failed to send OTP');
      throw error;
    }
  };

  private genRef(len = 5): string {
    const ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ';
    const out = Array.from({ length: len }, () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)]).join('');

    return out;
  }

  private startCountdown = () => {
    const currentDate = new Date();

    if (!this._lastCountdownValue) {
      this._isResendAvailable$.next(false);
      this._resendTimeout$.next(this.RESENT_TIMEOUT);
      this._lastCountdownValue = currentDate;
      setTimeout(this.startCountdown, 100);

      return;
    }

    const diff = differenceInSeconds(currentDate, this._lastCountdownValue);
    const resendTimeout = this._resendTimeout$.value;
    const secondsLeft = resendTimeout - diff;

    if (secondsLeft <= 0) {
      this._resendTimeout$.next(0);
      this._isResendAvailable$.next(true);
      this._lastCountdownValue = null;
      return;
    }

    if (diff > 0) {
      this._resendTimeout$.next(secondsLeft);
      this._lastCountdownValue = currentDate;
    }

    setTimeout(this.startCountdown, 100);
  };
}
